export interface TraffickingContext {
  campaignName: string
  dami: Record<string, any>
  assets: any[]
  processingJobs: Array<{ name: string; progress: number; result?: any; error?: string }>
  assetValidations: Record<string, { errors?: string[]; warnings?: string[] }>
  complianceChecks: Record<string, boolean>
  rotationMeta: Record<string, number>
}

export const TRAFF_HEADERS: string[] = [
  'Country','Language','Advertiser ID','Creative Agency','Creative Status','Activation Team','Creative Version','Customer Type','Campaign Type','Campaign Name','Creative Name','Indication','Channel','CTA/Button Description','URL','CTA 2','URL 2','Static Clickthrough URL','FDA Filing Number','AdChoices Approval','HTML5 DOFU Expiration Date (MM-DD-YYYY)','Creative Size','Creative ID','Taxonomy String','Rotation Strategy','Rotation Weight (%)','HTML5 Preview Frames','Static Backup Preview','Processing Status','Export Date'
]

export function appendTracking(url?: string): string {
  if (!url) return ''
  try {
    const u = new URL(url, typeof window !== 'undefined' ? window.location.origin : 'https://example.com')
    if (!u.searchParams.has('cmp')) u.searchParams.set('cmp','%ecid!')
    if (!u.searchParams.has('sid')) u.searchParams.set('sid','%esid!')
    if (!u.searchParams.has('pid')) u.searchParams.set('pid','%epid!')
    return u.toString()
  } catch {
    return `${url}${url.includes('?') ? '&' : '?'}cmp=%ecid!&sid=%esid!&pid=%epid!`
  }
}

export function buildTraffickingRows(ctx: TraffickingContext) {
  const timestamp = new Date().toISOString().split('T')[0]
  const { campaignName, dami, assets, processingJobs, assetValidations, complianceChecks, rotationMeta } = ctx

  const rows = (assets || []).map((asset: any) => {
    const job = processingJobs.find(j => j.name === asset.name)
    const v = assetValidations[asset.id] || { errors: [], warnings: [] }
    const dimensions = asset.dimensions || `${asset.width || 'N/A'}x${asset.height || 'N/A'}`
    const taxonomy = `${(dami.brand || 'brand')}_${(dami.product || 'product')}_${(asset.name || '')}`.replace(/\s+/g,'_').toLowerCase()
    const creativeId = taxonomy
    return {
      'Country': dami.country || 'USA',
      'Language': asset.language || dami.language || 'ENG',
      'Advertiser ID': dami.advertiserId || '',
      'Creative Agency': dami.creativeAgency || '',
      'Creative Status': dami.creativeStatus || 'New',
      'Activation Team': dami.activationTeam || 'Programmatic',
      'Creative Version': dami.creativeVersion || asset.version || 'V1',
      'Customer Type': asset.customerType || dami.customerType || 'Not Set',
      'Campaign Type': dami.campaignType || 'Branded',
      'Campaign Name': campaignName,
      'Creative Name': asset.name || '',
      'Indication': asset.indication || dami.indication || 'Not Set',
      'Channel': job?.result?.channelType || asset.channel || 'Display',
      'CTA/Button Description': asset.cta1 || (asset.mimeType?.split('/')[0] || 'Image'),
      'URL': appendTracking(asset.url1 || ''),
      'CTA 2': asset.cta2 || '',
      'URL 2': appendTracking(asset.url2 || ''),
      'Static Clickthrough URL': appendTracking(asset.staticUrl || ''),
      'FDA Filing Number': asset.fdaFilingNumber || dami.fdaFilingNumber || 'Missing',
      'AdChoices Approval': (complianceChecks.adChoicesApproval ? 'Y' : 'N'),
      'HTML5 DOFU Expiration Date (MM-DD-YYYY)': asset.dofuExpiration || '',
      'Creative Size': dimensions,
      'Creative ID': creativeId,
      'Taxonomy String': taxonomy,
      'Rotation Strategy': dami.rotationStrategy || 'DART',
      'Rotation Weight (%)': (rotationMeta[asset.id] ?? ''),
      'HTML5 Preview Frames': asset.thumbnailLink || '',
      'Static Backup Preview': asset.thumbnailLink || '',
      'Processing Status': job?.progress === 100 ? 'Complete' : job?.progress ? `${job.progress}% Processing` : 'Pending',
      'Export Date': timestamp
    }
  })

  return { headers: TRAFF_HEADERS, rows }
}

export function rowsToCSV(headers: string[], rows: any[]): string {
  return [
    headers.join(','),
    ...rows.map(row => headers.map(h => {
      const value = row[h] ?? ''
      const str = String(value)
      return '"' + str.replace(/"/g, '""') + '"'
    }).join(','))
  ].join('\n')
}
